import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import {
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/lib/table'
import { Link } from 'react-router-dom'
import { usePartnerContest } from './hooks'
import { disqualifyRequested, pointsChanged, stageChanged } from './model'
import type { ContestStage, Participant } from './types'
import { Guard } from 'src/features/session/guard'
import { GUARD_RULES } from 'src/features/session/constants'
import { path } from 'src/libs'
import { useUnit } from 'effector-react'
import {
  $countriesList,
  dictionariesMounted,
} from 'src/features/dictionaries/model'
import { transformCountriesToOptions } from 'src/libs/formatCountries'
import { useUrlFilters } from 'src/libs/useUrlFilters'
import Highlighter from 'react-highlight-words'
import * as React from 'react'

const regions = [
  'africa',
  'arabic_countries',
  'indonesia',
  'latam',
  'malaysia',
  'others',
]
const FILTER_CONFIG = {
  clientId: { type: 'string' as const },
  clientName: { type: 'string' as const },
  country: { type: 'array' as const },
  region: { type: 'array' as const },
  status: { type: 'array' as const },
  totalPoints: { type: 'string' as const },
  place: { type: 'string' as const },
  page: { type: 'string' as const, default: '1', pagination: true },
  pageSize: { type: 'string' as const, default: '100', pagination: true },
  sortField: { type: 'string' as const, default: 'place', pagination: true },
  sortOrder: { type: 'string' as const, default: 'ascend', pagination: true },
}

export const PartnerContest2026Page = () => {
  const {
    contest,
    participants,
    total,
    params,
    loading,
    paramsChanged,
    refresh,
  } = usePartnerContest()
  const { filters, setFilters } = useUrlFilters(FILTER_CONFIG)
  const [disqualified, setDisqualified] = useState<Participant | null>(null)
  const [editing, setEditing] = useState<Participant | null>(null)
  const [disqualifyForm] = Form.useForm()
  const [pointsForm] = Form.useForm()
  const countriesList = useUnit($countriesList)
  const countryOptions = useMemo(
    () =>
      transformCountriesToOptions(countriesList).map((country) => ({
        label: country.text,
        value: country.value,
      })),
    [countriesList],
  )
  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 100
  const orderBy = filters.sortField || 'place'
  const orderDirection = filters.sortOrder || 'ascend'

  useEffect(() => {
    dictionariesMounted()
  }, [])

  useEffect(() => {
    paramsChanged({
      page,
      limit: pageSize,
      orderBy,
      orderDirection,
      filters: {
        clientId: filters.clientId,
        clientName: filters.clientName,
        country: filters.country,
        region: filters.region,
        status: filters.status,
        totalPoints: filters.totalPoints,
        place: filters.place,
      },
    })
  }, [filters, page, pageSize, orderBy, orderDirection, paramsChanged])

  const handleTableChange = (
    pagination: any,
    tableFilters: Record<string, any>,
    sorter: any,
    extra: any,
  ) => {
    const filterUpdates = Object.fromEntries(
      Object.entries(tableFilters).map(([key, value]) => {
        if (!Array.isArray(value) || value.length === 0) return [key, undefined]
        return [
          key,
          key === 'clientName' || key === 'totalPoints' || key === 'place'
            ? String(value[0])
            : value.map(String),
        ]
      }),
    )
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const sortUpdates =
      currentSorter?.field && currentSorter.order
        ? {
            sortField: String(currentSorter.field),
            sortOrder: currentSorter.order,
          }
        : { sortField: undefined, sortOrder: undefined }

    const paginationUpdates =
      pagination.pageSize !== pageSize
        ? { pageSize: String(pagination.pageSize), page: undefined }
        : {
            page:
              extra.action === 'paginate' && pagination.current !== 1
                ? String(pagination.current)
                : undefined,
          }

    setFilters({
      ...filterUpdates,
      ...sortUpdates,
      ...paginationUpdates,
    } as any)
  }

  const columns: ColumnsType<Participant> = [
    {
      title: 'Client ID',
      key: 'clientId',
      fixed: 'left',
      filteredValue: filters.clientId ? [filters.clientId] : null,
      filterMultiple: false,
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <Space direction="vertical" style={{ padding: 8 }}>
          <Input
            autoFocus
            prefix={<SearchOutlined />}
            placeholder="Client ID"
            value={selectedKeys[0] ?? ''}
            onChange={(event) => {
              const value = event.target.value.replace(/\D/g, '')
              setSelectedKeys(value ? [value] : [])
            }}
            onPressEnter={() => confirm()}
            allowClear
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()}>
              Filter
            </Button>
            <Button size="small" onClick={() => clearFilters?.()}>
              Reset
            </Button>
          </Space>
        </Space>
      ),
      render: (_, record) => (
        <Link
          target="_blank"
          to={path.clients.details(String(record.client.clientId))}
        >
          {record.client.clientId}
        </Link>
      ),
    },
    {
      title: 'Client name',
      key: 'clientName',
      fixed: 'left',
      filteredValue: filters.clientName ? [filters.clientName] : null,
      filterMultiple: false,
      render: (_, record) => record.client.fullName ?? '—',
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <Space direction="vertical" style={{ padding: 8 }}>
          <Input
            autoFocus
            prefix={<SearchOutlined />}
            placeholder="Client name"
            value={selectedKeys[0] ?? ''}
            onChange={(event) => {
              const value = event.target.value
              setSelectedKeys(value ? [value] : [])
            }}
            onPressEnter={() => confirm()}
            allowClear
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()}>
              Filter
            </Button>
            <Button size="small" onClick={() => clearFilters?.()}>
              Reset
            </Button>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Country',
      key: 'country',
      filteredValue: filters.country ?? null,
      render: (_, record) => record.client.country ?? '—',
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <Space direction="vertical" style={{ padding: 8 }}>
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder="Country"
            style={{ minWidth: 240 }}
            value={selectedKeys as string[]}
            options={countryOptions}
            onChange={(values) => setSelectedKeys(values)}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()}>
              Filter
            </Button>
            <Button size="small" onClick={() => clearFilters?.()}>
              Reset
            </Button>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Region',
      dataIndex: 'region',
      filteredValue: filters.region ?? null,
      filters: regions.map((value) => ({ text: value, value })),
      filterMultiple: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filteredValue: filters.status ?? null,
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Disqualified', value: 'disqualified' },
      ],
      filterMultiple: true,
    },
    {
      title: 'Total points',
      dataIndex: 'totalPoints',
      filteredValue: filters.totalPoints ? [filters.totalPoints] : null,
      sorter: true,
      filters: [{ text: '>= 70', value: '>=70' }],
      filterMultiple: false,
      render: (value, record) => (
        <Space>
          {value}
          <Guard permission={GUARD_RULES.PARTNER_CONTEST_2026_POINTS}>
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditing(record)
                pointsForm.setFieldsValue({ totalPoints: value })
              }}
            />
          </Guard>
        </Space>
      ),
    },
    { title: 'New deposits', dataIndex: 'newDepositsPoints', sorter: true },
    { title: 'Lots traded', dataIndex: 'lotsPoints', sorter: true },
    { title: 'Reactivation', dataIndex: 'reactivationPoints', sorter: true },
    {
      title: 'Place',
      dataIndex: 'place',
      filteredValue: filters.place ? [filters.place] : null,
      sorter: true,
      sortOrder:
        orderBy === 'place'
          ? (orderDirection as 'ascend' | 'descend')
          : undefined,
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <Space direction="vertical" style={{ padding: 8 }}>
          <InputNumber
            min={1}
            precision={0}
            step={1}
            placeholder="Place"
            style={{ width: '100%' }}
            value={selectedKeys[0] ? Number(selectedKeys[0]) : null}
            onChange={(value) =>
              setSelectedKeys(value === null ? [] : [String(value)])
            }
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()}>
              Filter
            </Button>
            <Button size="small" onClick={() => clearFilters?.()}>
              Reset
            </Button>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Cash',
      dataIndex: 'cash',
      sorter: true,
      render: (value) => value ?? '—',
    },
    {
      title: 'Disqualify',
      fixed: 'right',
      render: (_, record) =>
        record.status.toLowerCase() === 'active' ? (
          <Guard permission={GUARD_RULES.PARTNER_CONTEST_2026_STATUS}>
            <Button danger size="small" onClick={() => setDisqualified(record)}>
              Disqualify
            </Button>
          </Guard>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ]

  const currentStage = contest?.status?.toLowerCase() as
    | ContestStage
    | undefined
  const nextStage =
    currentStage === 'active'
      ? 'ended'
      : currentStage === 'ended'
      ? 'results'
      : null

  const submitDisqualification = async () => {
    const values = await disqualifyForm.validateFields()
    disqualifyRequested({
      clientId: disqualified!.client.clientId,
      reason: values.reason,
    })
    setDisqualified(null)
    disqualifyForm.resetFields()
  }
  const submitPoints = async () => {
    const values = await pointsForm.validateFields()
    const totalPoints = Number(values.totalPoints)
    pointsChanged({ clientId: editing!.client.clientId, totalPoints })
    setEditing(null)
    pointsForm.resetFields()
  }
  const exportCsv = () => {
    const headers = [
      'Client ID',
      'Country',
      'Region',
      'Status',
      'Total points',
      'New deposits',
      'Lots traded',
      'Reactivation',
      'Place',
      'Cash',
    ]
    const rows = participants.map((item) => [
      item.client.clientId,
      item.client.country ?? '',
      item.region,
      item.status,
      item.totalPoints,
      item.newDepositsPoints,
      item.lotsPoints,
      item.reactivationPoints,
      item.place ?? '',
      item.cash ?? '',
    ])
    const csv = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','),
      )
      .join('\n')
    const link = document.createElement('a')
    link.href = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8' }),
    )
    link.download = 'partner-contest-2026.csv'
    link.click()
    URL.revokeObjectURL(link.href)
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {contest?.name ?? 'Partner Contest 2026'}
          </Typography.Title>
          <Typography.Text type="secondary">
            {contest
              ? `${formatTs(contest.startTs)} — ${formatTs(
                  contest.endTs,
                )} · regional leaderboard`
              : 'Loading contest details…'}
          </Typography.Text>
        </div>
        <Space wrap>
          <Tag
            color={
              currentStage === 'active'
                ? 'green'
                : currentStage === 'ended'
                ? 'orange'
                : 'blue'
            }
          >
            {currentStage ?? '—'}
          </Tag>
          <Guard permission={GUARD_RULES.PARTNER_CONTEST_2026_STAGE}>
            <Button
              disabled={!nextStage}
              onClick={() =>
                Modal.confirm({
                  title: `Change stage to ${nextStage}?`,
                  icon: <ExclamationCircleOutlined />,
                  onOk: () => stageChanged(nextStage!),
                })
              }
            >
              Change stage
            </Button>
          </Guard>
          <Button icon={<ReloadOutlined />} onClick={() => refresh()}>
            Refresh
          </Button>
          <Guard permission={GUARD_RULES.PARTNER_CONTEST_2026_EXPORT}>
            <Button icon={<DownloadOutlined />} onClick={exportCsv}>
              Export
            </Button>
          </Guard>
        </Space>
      </Space>
      <Table<Participant>
        rowKey={(record) => String(record.client.clientId)}
        loading={loading}
        dataSource={participants}
        sticky={{ offsetHeader: 64 }}
        scroll={{ x: 1200 }}
        pagination={{
          total,
          current: params.page,
          pageSize: params.limit,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        columns={columns}
      />
      <Modal
        open={Boolean(disqualified)}
        title="Disqualify participant"
        okText="Disqualify"
        okButtonProps={{ danger: true }}
        onOk={submitDisqualification}
        onCancel={() => {
          setDisqualified(null)
          disqualifyForm.resetFields()
        }}
      >
        <Form form={disqualifyForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason"
            rules={[
              { required: true, whitespace: true, message: 'Enter a reason' },
              { max: 1000, message: 'Reason cannot exceed 1000 characters' },
            ]}
          >
            <Input.TextArea rows={4} maxLength={1000} showCount autoFocus />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={Boolean(editing)}
        title="Adjust total points"
        okText="Save"
        onOk={submitPoints}
        onCancel={() => {
          setEditing(null)
          pointsForm.resetFields()
        }}
      >
        <Form form={pointsForm} layout="vertical">
          <Form.Item
            name="totalPoints"
            label="Total points"
            normalize={(value) => String(value ?? '').replace(/\D/g, '')}
            rules={[
              { required: true, message: 'Enter total points' },
              {
                validator: (_, value) =>
                  value === '' || /^\d+$/.test(value)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          'Enter a whole number greater than or equal to 0',
                        ),
                      ),
              },
            ]}
          >
            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}

function formatTs(value: string) {
  const timestamp = Number(value)
  if (!Number.isFinite(timestamp)) return value
  return new Date(timestamp * 1000).toLocaleDateString()
}
